import { memo, useMemo, useRef, type ReactNode, type RefObject } from "react";
import styled from "styled-components";

import { isRenderableNode } from "../../utils/reactNode";
import { AnnotationIcon, CameraIcon, EyeIcon, TrashIcon } from "../../icons/glyphs";
import { TooltipIconButton } from "../IconButton";
import { MediaTimelineBar, type MediaTimelineMarker } from "../MediaTimelineBar";
import { Spinner } from "../Spinner";
import { Text, defaultTextVariants } from "../Text";
import type { PlaybackTransport } from "../Video";
import { annotationStudioText } from "./messages";
import type { AnnotationStudioLocale, AnnotationStudioViewMode } from "./types";

export interface StudioMediaControlsProps {
  readonly locale: AnnotationStudioLocale;
  readonly playbackIdentity: string;
  readonly viewMode: AnnotationStudioViewMode;
  readonly playbackTransport: PlaybackTransport;
  readonly onPrimaryPlaybackToggle?: () => void;
  readonly streamAvailable: boolean;
  /** Whether the active timeline targets the controller's selected sequence and stream. */
  readonly timelineContextActive: boolean;
  readonly resultPlaybackActive: boolean;
  readonly markers?: readonly MediaTimelineMarker[];
  readonly timelineAction?: ReactNode;
  readonly authoring: boolean;
  readonly frameAtPlayheadId: string | null;
  readonly previewFrameId: string | null;
  readonly capturePending: boolean;
  readonly captureDisabled: boolean;
  readonly authoringDisabledReasonId?: string;
  readonly removeAvailable: boolean;
  readonly removeDisabled: boolean;
  readonly captureButtonRef: RefObject<HTMLButtonElement | null>;
  readonly onMarkerSelect: (frameId: string) => void;
  readonly onToggleCapturedFramePreview: (frameId: string) => void;
  readonly onEditCapturedFrame: (frameId: string) => void;
  readonly onBrowseVideoRequest: () => void;
  readonly onCapture: (timeSeconds?: number) => void;
  readonly onRequestRemove: (frameId: string) => void;
}

// The bar paints no surface of its own; the studio's footer is the recessed panel it sits on.
const PlaybackFooter = styled.div`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  min-width: 0;
  flex: 0 0 auto;
  padding: 0.5rem 0.75rem 0.6rem;
  background: ${({ theme }) => theme.color.bg.surfaceSunken};
`;

const CaptureActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.35rem;

  & > button {
    flex: 0 0 auto;
  }

  & > button > span {
    white-space: nowrap;
  }
`;

const AvailabilityMessage = styled(Text)`
  padding: 0.4rem 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.color.border.subtle};
  background: ${({ theme }) => theme.color.bg.surfaceRaised};
  color: ${({ theme }) => theme.color.text.muted};
`;

function StudioMediaControlsComponent({
  locale,
  playbackIdentity,
  viewMode,
  playbackTransport,
  onPrimaryPlaybackToggle,
  streamAvailable,
  timelineContextActive,
  resultPlaybackActive,
  markers,
  timelineAction,
  authoring,
  frameAtPlayheadId,
  previewFrameId,
  capturePending,
  captureDisabled,
  authoringDisabledReasonId,
  removeAvailable,
  removeDisabled,
  captureButtonRef,
  onMarkerSelect,
  onToggleCapturedFramePreview,
  onEditCapturedFrame,
  onBrowseVideoRequest,
  onCapture,
  onRequestRemove,
}: StudioMediaControlsProps) {
  const captureActionVisible = timelineContextActive && authoring && frameAtPlayheadId === null;
  const captureActionDisabled = !streamAvailable || capturePending || captureDisabled;
  const previewActionVisible = timelineContextActive && frameAtPlayheadId !== null;
  const annotationActionVisible =
    timelineContextActive && viewMode === "browse" && frameAtPlayheadId !== null;
  const annotationActionDisabled = authoring && captureDisabled;
  const removeActionVisible =
    timelineContextActive && authoring && frameAtPlayheadId !== null && removeAvailable;
  const contextualActionsVisible =
    captureActionVisible ||
    previewActionVisible ||
    annotationActionVisible ||
    removeActionVisible ||
    isRenderableNode(timelineAction);
  const previewingPlayheadFrame =
    frameAtPlayheadId !== null && previewFrameId === frameAtPlayheadId;
  const onBrowseVideoRequestRef = useRef(onBrowseVideoRequest);
  onBrowseVideoRequestRef.current = onBrowseVideoRequest;
  const intentAwareTransport = useMemo<PlaybackTransport>(
    () =>
      resultPlaybackActive
        ? playbackTransport
        : {
            subscribe: playbackTransport.subscribe,
            getSnapshot: playbackTransport.getSnapshot,
            play: () => {
              onBrowseVideoRequestRef.current();
              playbackTransport.play();
            },
            pause: playbackTransport.pause,
            toggle: () => {
              if (!playbackTransport.getSnapshot().playing) {
                onBrowseVideoRequestRef.current();
              }
              playbackTransport.toggle();
            },
            seekToSeconds: (seconds) => {
              onBrowseVideoRequestRef.current();
              playbackTransport.seekToSeconds(seconds);
            },
            setPlaybackRate: playbackTransport.setPlaybackRate,
            ...(playbackTransport.goLive
              ? {
                  goLive: () => {
                    onBrowseVideoRequestRef.current();
                    playbackTransport.goLive?.();
                  },
                }
              : {}),
            reset: () => {
              onBrowseVideoRequestRef.current();
              playbackTransport.reset();
            },
          },
    [playbackTransport, resultPlaybackActive],
  );

  return (
    <PlaybackFooter data-studio-playback-footer="">
      <MediaTimelineBar
        key={playbackIdentity}
        transport={intentAwareTransport}
        onPrimaryPlaybackToggle={onPrimaryPlaybackToggle}
        stepSeconds={1 / 30}
        disabled={!streamAvailable}
        markers={markers}
        onMarkerCreate={captureActionVisible && !captureActionDisabled ? onCapture : undefined}
        onMarkerSelect={
          timelineContextActive && markers && markers.length > 0 ? onMarkerSelect : undefined
        }
        onMarkerRemove={removeActionVisible && !removeDisabled ? onRequestRemove : undefined}
        trailingControl={
          contextualActionsVisible ? (
            <CaptureActions data-frame-capture-actions="">
              {captureActionVisible ? (
                <TooltipIconButton
                  ref={captureButtonRef}
                  data-frame-capture-action=""
                  size="md"
                  variant="solid"
                  tooltip={{
                    content: annotationStudioText("captureFrame", locale),
                    placement: "top",
                  }}
                  aria-label={annotationStudioText("captureFrame", locale)}
                  aria-keyshortcuts={captureActionDisabled ? undefined : "Shift+C"}
                  aria-describedby={captureActionDisabled ? authoringDisabledReasonId : undefined}
                  disabled={captureActionDisabled}
                  onClick={() => onCapture()}
                >
                  {capturePending ? (
                    <Spinner label={annotationStudioText("capturingFrame", locale)} size="sm" />
                  ) : (
                    <CameraIcon />
                  )}
                </TooltipIconButton>
              ) : null}
              {annotationActionVisible ? (
                <TooltipIconButton
                  data-playhead-annotation-action=""
                  size="md"
                  variant="solid"
                  tooltip={{
                    content: annotationStudioText(
                      authoring ? "annotationEditMode" : "viewAnnotation",
                      locale,
                    ),
                    placement: "top",
                  }}
                  aria-label={annotationStudioText(
                    authoring ? "editPlayheadAnnotation" : "viewPlayheadAnnotation",
                    locale,
                  )}
                  aria-keyshortcuts="Shift+C"
                  aria-describedby={
                    annotationActionDisabled ? authoringDisabledReasonId : undefined
                  }
                  disabled={annotationActionDisabled}
                  onClick={() => {
                    if (frameAtPlayheadId !== null) onEditCapturedFrame(frameAtPlayheadId);
                  }}
                >
                  <AnnotationIcon aria-hidden="true" />
                </TooltipIconButton>
              ) : null}
              {previewActionVisible ? (
                <TooltipIconButton
                  data-playhead-preview-action=""
                  data-playhead-preview-current={previewingPlayheadFrame ? "" : undefined}
                  size="md"
                  variant="solid"
                  tone="secondary"
                  selected={previewingPlayheadFrame}
                  tooltip={{
                    content: annotationStudioText("framePreview", locale),
                    placement: "top",
                  }}
                  aria-label={annotationStudioText("previewCapturedFrame", locale)}
                  onClick={() => {
                    if (frameAtPlayheadId !== null) {
                      onToggleCapturedFramePreview(frameAtPlayheadId);
                    }
                  }}
                >
                  <EyeIcon aria-hidden="true" />
                </TooltipIconButton>
              ) : null}
              {timelineAction}
              {removeActionVisible ? (
                <TooltipIconButton
                  data-playhead-frame-remove-action=""
                  size="md"
                  variant="surface"
                  tone="danger"
                  border
                  tooltip={{
                    content: annotationStudioText("removeCapturedFrame", locale),
                    placement: "top",
                  }}
                  aria-label={annotationStudioText("removeCapturedFrame", locale)}
                  aria-keyshortcuts={removeDisabled ? undefined : "Delete"}
                  aria-describedby={removeDisabled ? authoringDisabledReasonId : undefined}
                  disabled={removeDisabled}
                  onClick={() => {
                    if (frameAtPlayheadId !== null) onRequestRemove(frameAtPlayheadId);
                  }}
                >
                  <TrashIcon aria-hidden="true" />
                </TooltipIconButton>
              ) : null}
            </CaptureActions>
          ) : undefined
        }
        labels={{
          seek: annotationStudioText("playback", locale),
          markers: annotationStudioText("capturedFrameEvents", locale),
          nearestMarker: annotationStudioText("nearestCapturedFrame", locale),
          timelineZoom: annotationStudioText("timelineZoom", locale),
          zoomIn: annotationStudioText("timelineZoomIn", locale),
          zoomOut: annotationStudioText("timelineZoomOut", locale),
          resetZoom: annotationStudioText("resetTimelineZoom", locale),
          playbackRate: annotationStudioText("playbackRate", locale),
          previousTimelineSegment: annotationStudioText("previousTimelineSegment", locale),
          nextTimelineSegment: annotationStudioText("nextTimelineSegment", locale),
        }}
      />
    </PlaybackFooter>
  );
}

export function StudioAuthoringAvailabilityMessage({
  id,
  message,
}: {
  readonly id: string;
  readonly message?: string;
}) {
  return message ? (
    <AvailabilityMessage
      id={id}
      role="status"
      variant={defaultTextVariants["body-normal"]}
      data-authoring-availability-reason=""
    >
      {message}
    </AvailabilityMessage>
  ) : null;
}

export const StudioMediaControls = memo(StudioMediaControlsComponent);
